import { DataGridPro } from '@mui/x-data-grid-pro';
import { Context } from './Context';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { Filter } from 'components/filter/Filter';
import { ContextMenu } from 'components/context_menu/ContextMenu';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MediationIcon from '@mui/icons-material/Mediation';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { AssignTag } from 'components/dialog/employee/AssignTag';
import { AddEmployee } from 'components/dialog/employee/AddEmployee';
import { EditEmployee } from 'components/dialog/employee/EditEmployee';
import { AssignContract } from 'components/dialog/employee/AssignContract';
import { AssingPosition } from 'components/dialog/employee/AssingPosition';
import { DeactivateEmployee } from 'components/dialog/employee/DeactivateEmployee';
import { AssignAccrualProfile } from 'components/dialog/employee/AssignAccrualProfile';
import { AssignScheduleRule } from 'components/dialog/employee/AssignScheduleRule';
import { AssignPayRule } from 'components/dialog/employee/AssignPayRule';
import { AssignPositionNew } from 'components/dialog/employee/AssignPositionNew';
import { buildTimekeeperEmployeeActionPath, buildTimekeeperEmployeeBasePath, parseTimekeeperEmployeeActionRoute } from 'utils/timekeeperEmployeeRouting';

const Table = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const {
        columns,
        rows,
        checked,
        setChecked,
        contextMenu,
        setContextMenu,
        openAssignTag,
        setOpenAssignTag,
        openAssignAccrualProfile,
        setOpenAssignAccrualProfile,
        openAddEmployee,
        setOpenAddEmployee,
        openEditEmployee,
        setOpenEditEmployee,
        openAssignContract,
        setOpenAssignContract,
        openAssignPosition,
        setOpenAssignPosition,
        openDeactivateEmployee,
        setOpenDeactivateEmployee,
        reloading,
        loadingGettingEmployeeInfo,
        openAssignScheduleRule,
        setOpenAssignScheduleRule,
        openAssignPayRule,
        setOpenAssignPayRule,
    } = useContext(Context);

    // Estado local para el nuevo diálogo hardcodeado ("Agregar puesto 1") mientras se define su ruta/contexto definitivo.
    const [openAssignPositionNew, setOpenAssignPositionNew] = useState(false);
    const [openAssignPositionNew2, setOpenAssignPositionNew2] = useState(false);

    const basePath = buildTimekeeperEmployeeBasePath();
    const routeAction = useMemo(() => parseTimekeeperEmployeeActionRoute(location.pathname), [location.pathname]);

    const selectedRowsFromChecked = useMemo(() => rows.filter((row) => checked.includes(row.id)), [rows, checked]);
    const selectedRowFromRoute = useMemo(() => {
        if (routeAction.id === null) return null;
        return rows.find((row) => Number(row.id) === routeAction.id || Number(row.employee_id) === routeAction.id) || null;
    }, [rows, routeAction.id]);

    const selectedRowsForDialogs = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRowsFromChecked;
    const selectedDataForSingle = selectedRowsForDialogs[0];

    const closeRouteOrLocal = useCallback(
        (routeKey, setLocalOpen) => (nextOpen) => {
            if (nextOpen) {
                setLocalOpen(true);
                return;
            }

            if (routeAction.action === routeKey) {
                navigate(basePath);
                return;
            }

            setLocalOpen(false);
        },
        [routeAction.action, navigate, basePath],
    );

    const options = useMemo(
        () => [
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.add'),
                click: () => {
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('add'));
                },
                icon: <AddIcon />,
                group: 1,
                disabled: false,
                seleted: false,
                onlyOne: false,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.edit'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('edit', current.id));
                },
                icon: <EditIcon />,
                group: 1,
                disabled: checked.length < 1,
                seleted: false,
                onlyOne: true,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.delete'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('deactivate', current.id));
                },
                icon: <PersonOffIcon />,
                group: 1,
                disabled: checked.length !== 1 || rows.find((row) => row.id === checked[0])?.active === false,
                seleted: false,
                onlyOne: true,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_contract'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('assign-contract', current.id));
                },
                icon: <HistoryEduIcon />,
                group: 1,
                disabled: checked.length !== 1,
                seleted: false,
                onlyOne: true,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_position'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    setOpenAssignPositionNew(true);
                },
                icon: <MediationIcon />,
                group: 1,
                disabled: checked.length !== 1,
                seleted: false,
                onlyOne: true,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_tag'),
                click: () => {
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('assign-tag'));
                },
                icon: <LocalOfferIcon />,
                group: 1,
                disabled: checked.length < 1,
                seleted: true,
                onlyOne: false,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_accrual_profile'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('assign-accrual-profile', current?.id));
                },
                icon: <ManageAccountsIcon />,
                group: 1,
                disabled: checked.length < 1,
                seleted: true,
                onlyOne: false,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_schedule_rule'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('assign-schedule-rule', current?.id));
                },
                icon: <ScheduleSendIcon />,
                group: 1,
                disabled: checked.length < 1,
                seleted: true,
                onlyOne: false,
                dialog: undefined,
            },
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.assign_pay_rule'),
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    navigate(buildTimekeeperEmployeeActionPath('assign-pay-rule', current.id));
                },
                icon: <ManageAccountsIcon />,
                group: 1,
                disabled: checked.length !== 1,
                seleted: true,
                onlyOne: true,
                dialog: undefined,
            },
            /* {
                privilege: true,
                name: 'Agregar puesto 1',
                click: () => {
                    const current = selectedRowsFromChecked[0];
                    if (!current?.id) return;
                    setContextMenu(null);
                    setOpenAssignPositionNew(true);
                },
                icon: <MediationIcon />,
                group: 1,
                disabled: checked.length !== 1,
                seleted: false,
                onlyOne: true,
                dialog: undefined,
            }, */
            {
                privilege: true,
                name: t('timekeeper.employees.table.context_menu.reload'),
                click: () => {
                    setContextMenu(null);
                    reloading();
                },
                icon: <RefreshIcon />,
                group: 3,
                disabled: false,
                seleted: false,
                onlyOne: false,
                dialog: undefined,
            },
        ],
        [
            t,
            checked,
            rows,
            setContextMenu,
            setOpenAddEmployee,
            setOpenEditEmployee,
            setOpenDeactivateEmployee,
            setOpenAssignContract,
            setOpenAssignPosition,
            setOpenAssignTag,
            setOpenAssignAccrualProfile,
            setOpenAssignScheduleRule,
            setOpenAssignPayRule,
            reloading,
            navigate,
            selectedRowsFromChecked,
        ],
    );

    return (
        <>
            <div
                onContextMenu={(event) => {
                    event.preventDefault();
                    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
                }}
            >
                <DataGridPro
                    sx={{ height: `calc(100vh - 120px)` }}
                    showCellVerticalBorder
                    showColumnVerticalBorder
                    checkboxSelection
                    loading={loadingGettingEmployeeInfo}
                    columns={columns}
                    rows={rows}
                    slots={{
                        toolbar: useCallback(() => <Filter view={'employee'} />, []),
                        loadingOverlay: LinearProgress,
                        //columnMenu: CustomColumnMenu,
                    }}
                    rowSelectionModel={checked}
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        setChecked(newRowSelectionModel);
                    }}
                    hideFooter
                    disableRowSelectionOnClick
                    getRowHeight={(params) => {
                        const { model } = params;

                        let length = 64;

                        for (const key of Object.keys(model).filter((k) => Array.isArray(model[k]))) {
                            if (key === 'org_path' || key === 'hire_date') {
                                //console.log(key, model[key]);

                                let counter = 0;

                                if (model[key].length > 0) {
                                    counter = counter + model[key].length * 55;
                                }

                                if (counter > length) {
                                    length = counter;
                                }
                            }
                        }

                        return length;
                    }}
                />

                <AssignTag
                    open={(openAssignTag || routeAction.action === 'assign-tag') && selectedRowsForDialogs.length > 0}
                    setOpen={closeRouteOrLocal('assign-tag', setOpenAssignTag)}
                    data={rows}
                    checked={selectedRowsForDialogs}
                    reloading={reloading}
                />

                <AddEmployee open={openAddEmployee || routeAction.action === 'add'} setOpen={closeRouteOrLocal('add', setOpenAddEmployee)} reloading={reloading} />

                <EditEmployee
                    open={(openEditEmployee || routeAction.action === 'edit') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('edit', setOpenEditEmployee)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                    rows={rows}
                />

                <AssignContract
                    open={(openAssignContract || routeAction.action === 'assign-contract') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('assign-contract', setOpenAssignContract)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                />

                <AssingPosition
                    open={(openAssignPosition || routeAction.action === 'assign-position') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('assign-position', setOpenAssignPosition)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                />

                <DeactivateEmployee
                    open={(openDeactivateEmployee || routeAction.action === 'deactivate') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('deactivate', setOpenDeactivateEmployee)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                />

                <AssignAccrualProfile
                    open={(openAssignAccrualProfile || routeAction.action === 'assign-accrual-profile') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('assign-accrual-profile', setOpenAssignAccrualProfile)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                    cleanSelected={() => setChecked([])}
                />

                <AssignScheduleRule
                    open={(openAssignScheduleRule || routeAction.action === 'assign-schedule-rule') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('assign-schedule-rule', setOpenAssignScheduleRule)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                    cleanSelected={() => setChecked([])}
                />

                <AssignPayRule
                    open={(openAssignPayRule || routeAction.action === 'assign-pay-rule') && !!selectedDataForSingle}
                    setOpen={closeRouteOrLocal('assign-pay-rule', setOpenAssignPayRule)}
                    reloading={reloading}
                    data={selectedDataForSingle}
                    cleanSelected={() => setChecked([])}
                />
                <AssignPositionNew open={openAssignPositionNew && !!selectedDataForSingle} setOpen={setOpenAssignPositionNew} reloading={reloading} data={selectedDataForSingle} />
            </div>

            <ContextMenu contextMenu={contextMenu} setContextMenu={setContextMenu} options={options} checked={checked} />
        </>
    );
};

export { Table };
